import { useCallback, useEffect, useState } from "react";
import {
    Plus,
    ClipboardCheck,
    ClipboardList,
    Archive,
    ArchiveRestore,
    ArrowRight,
    ChevronRight,
    User,
    Calendar,
    AlertTriangle,
    MoreVertical,
    Loader2,
    CheckCircle2,
    Undo2,
    Trash2,
    Pencil,
    Mail,
    MailCheck,
    MailX,
    MailWarning,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/common/EmptyState";
import { OPS } from "@/constants/testIds";
import { toast } from "sonner";
import StartRoundDialog from "./StartRoundDialog";
import EditRoundMetaDialog from "./EditRoundMetaDialog";

/**
 * Landing view for QC rounds:
 *   - "Start new round" button (inspector + super_admin only)
 *   - My drafts (only for the caller — resumeable) — never shown to a pure viewer
 *   - Submitted rounds (all-visible-to-caller — read-only)
 *   - Super-admin-only row menu on every round: Edit meta / Force complete /
 *     Reopen / Archive / Delete. Server enforces the role on every action.
 *   - Super-admin-only "Show archived" toggle reveals archived rounds with
 *     an Unarchive action.
 */
export default function QCRoundsView({ onOpenRound }) {
    const { me, isSuperAdmin, isInspector, hasCap, canQcRoundAdmin } = useAuth();
    const { t, tn, lang } = useI18n();
    const [drafts, setDrafts] = useState(null);
    const [submitted, setSubmitted] = useState(null);
    const [archivedRounds, setArchivedRounds] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [startOpen, setStartOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    // Super-admin actions state
    const [confirm, setConfirm] = useState(null); // {action, round}
    const [busy, setBusy] = useState(false);
    const [editRound, setEditRound] = useState(null); // round object for meta edit

    // Who may start a round mirrors the backend `qc_can_submit` gate:
    // super_admin OR an inspector (role) OR anyone holding the `qc_submit`
    // capability.
    const canStart = isSuperAdmin || isInspector || hasCap("qc_submit");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [d, s, a] = await Promise.all([
                canStart
                    ? api.get("/qc-inspection/inspections/my-drafts").then((r) => r.data || [])
                    : Promise.resolve([]),
                api.get("/qc-inspection/inspections", { params: { status: "submitted", limit: 30 } })
                    .then((r) => r.data || []),
                canQcRoundAdmin && showArchived
                    ? api.get("/qc-inspection/inspections", { params: { archived: "only", limit: 100 } })
                        .then((r) => r.data || [])
                    : Promise.resolve(null),
            ]);
            setDrafts(d);
            setSubmitted(s);
            setArchivedRounds(a);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setLoading(false);
        }
    }, [t, canStart, canQcRoundAdmin, showArchived]);

    useEffect(() => {
        load();
    }, [load]);

    const onStarted = (round) => {
        setStartOpen(false);
        onOpenRound(round.id);
    };

    // --- super-admin actions ---
    const runAction = async () => {
        if (!confirm || busy) return;
        const { action, round } = confirm;
        setBusy(true);
        try {
            if (action === "reopen") {
                await api.post(`/qc-inspection/inspections/${round.id}/reopen`);
                toast.success(t("qc_admin_reopen_ok"));
            } else if (action === "force-submit") {
                await api.post(`/qc-inspection/inspections/${round.id}/force-submit`);
                toast.success(t("qc_admin_close_ok"));
            } else if (action === "delete" || action === "delete-draft") {
                await api.delete(`/qc-inspection/inspections/${round.id}`);
                toast.success(t("qc_admin_delete_ok"));
            } else if (action === "archive") {
                await api.post(`/qc-inspection/inspections/${round.id}/archive`);
                toast.success(t("qc_admin_archive_ok"));
            } else if (action === "unarchive") {
                await api.post(`/qc-inspection/inspections/${round.id}/unarchive`);
                toast.success(t("qc_admin_unarchive_ok"));
            }
            setConfirm(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setBusy(false);
        }
    };

    const openEdit = (round) => setEditRound(round);

    // Confirm dialog copy — driven by the action verb
    const confirmCopy = () => {
        if (!confirm) return { title: "", desc: "" };
        switch (confirm.action) {
            case "delete":
                return { title: t("qc_admin_delete_title"), desc: t("qc_admin_delete_desc") };
            case "delete-draft":
                return { title: t("qc_admin_delete_draft_title"), desc: t("qc_admin_delete_draft_desc") };
            case "reopen":
                return { title: t("qc_admin_reopen_title"), desc: t("qc_admin_reopen_desc") };
            case "archive":
                return { title: t("qc_admin_archive_title"), desc: t("qc_admin_archive_desc") };
            case "unarchive":
                return { title: t("qc_admin_unarchive_title"), desc: t("qc_admin_unarchive_desc") };
            default:
                return { title: t("qc_admin_close_title"), desc: t("qc_admin_close_desc") };
        }
    };

    return (
        <div className="space-y-5">
            {canStart ? (
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h2 className="text-base font-bold text-[color:var(--ink)]">{t("qc_ins_rounds_title")}</h2>
                        <p className="text-xs text-[color:var(--ink-muted)]">{t("qc_ins_rounds_desc")}</p>
                    </div>
                    <Button
                        data-testid={OPS.qcIns.rounds.startBtn}
                        onClick={() => setStartOpen(true)}
                        className="gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        {t("qc_ins_new_round")}
                    </Button>
                </div>
            ) : null}

            {/* Round admins (super or QC manager) — show archived toggle */}
            {canQcRoundAdmin ? (
                <div className="flex items-center justify-end gap-2 text-xs text-[color:var(--ink-muted)]">
                    <label
                        htmlFor="qc-show-archived"
                        className="inline-flex items-center gap-2 cursor-pointer select-none"
                    >
                        <Archive className="h-3.5 w-3.5" />
                        {t("qc_rounds_show_archived")}
                    </label>
                    <Switch
                        id="qc-show-archived"
                        data-testid={OPS.qcIns.rounds.showArchivedToggle}
                        checked={showArchived}
                        onCheckedChange={setShowArchived}
                    />
                </div>
            ) : null}

            {/* Drafts section */}
            {canStart ? (
                <section data-testid={OPS.qcIns.rounds.draftsSection} className="space-y-3">
                    <SectionHeader
                        icon={ClipboardCheck}
                        label={t("qc_ins_my_drafts")}
                        count={drafts?.length ?? null}
                    />
                    {loading && drafts === null ? (
                        <Skeleton className="h-24 w-full" />
                    ) : drafts && drafts.length === 0 ? (
                        <EmptyLine text={t("qc_ins_no_drafts")} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {drafts?.map((r) => (
                                <RoundCard
                                    key={r.id}
                                    round={r}
                                    lang={lang}
                                    tn={tn}
                                    t={t}
                                    onOpen={() => onOpenRound(r.id)}
                                    variant="draft"
                                    canRoundAdmin={canQcRoundAdmin}
                                    canDelete={isSuperAdmin}
                                    canDeleteDraft={canQcRoundAdmin || r.inspector_user_id === me?.user?.id}
                                    onEdit={openEdit}
                                    onAction={(action) => setConfirm({ action, round: r })}
                                />
                            ))}
                        </div>
                    )}
                </section>
            ) : null}

            {/* Submitted section */}
            <section data-testid={OPS.qcIns.rounds.submittedSection} className="space-y-3">
                <SectionHeader
                    icon={ClipboardList}
                    label={t("qc_ins_submitted_rounds")}
                    count={submitted?.length ?? null}
                />
                {loading && submitted === null ? (
                    <Skeleton className="h-24 w-full" />
                ) : submitted && submitted.length === 0 ? (
                    <EmptyState
                        icon={ClipboardList}
                        title={t("qc_ins_no_submitted")}
                        hint={canStart ? t("qc_ins_no_submitted_hint_inspector") : t("qc_ins_no_submitted_hint_viewer")}
                        dataset="qc_inspections"
                    />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {submitted?.map((r) => (
                            <RoundCard
                                key={r.id}
                                round={r}
                                lang={lang}
                                tn={tn}
                                t={t}
                                onOpen={() => onOpenRound(r.id)}
                                variant="submitted"
                                canRoundAdmin={canQcRoundAdmin}
                                canDelete={isSuperAdmin}
                                onEdit={openEdit}
                                onAction={(action) => setConfirm({ action, round: r })}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Archived section (super-admin only, when toggled on) */}
            {canQcRoundAdmin && showArchived ? (
                <section data-testid={OPS.qcIns.rounds.archivedSection} className="space-y-3">
                    <SectionHeader
                        icon={Archive}
                        label={t("qc_rounds_archived_section")}
                        count={archivedRounds?.length ?? null}
                    />
                    {loading && archivedRounds === null ? (
                        <Skeleton className="h-24 w-full" />
                    ) : archivedRounds && archivedRounds.length === 0 ? (
                        <EmptyLine text={t("qc_rounds_no_archived")} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {archivedRounds?.map((r) => (
                                <RoundCard
                                    key={r.id}
                                    round={r}
                                    lang={lang}
                                    tn={tn}
                                    t={t}
                                    onOpen={() => onOpenRound(r.id)}
                                    variant="archived"
                                    canRoundAdmin={canQcRoundAdmin}
                                    canDelete={isSuperAdmin}
                                    onEdit={openEdit}
                                    onAction={(action) => setConfirm({ action, round: r })}
                                />
                            ))}
                        </div>
                    )}
                </section>
            ) : null}

            <StartRoundDialog open={startOpen} onOpenChange={setStartOpen} onStarted={onStarted} />

            <EditRoundMetaDialog
                round={editRound}
                onClose={() => setEditRound(null)}
                onSaved={async () => {
                    setEditRound(null);
                    await load();
                }}
            />

            {/* Confirmation dialog for reopen / force-submit / delete / archive / unarchive */}
            <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle data-testid={OPS.qcIns.rounds.admin.confirmTitle}>
                            {confirmCopy().title}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmCopy().desc}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid={OPS.qcIns.rounds.admin.confirmBtn}
                            onClick={runAction}
                            disabled={busy}
                            className={
                                confirm?.action === "delete" || confirm?.action === "delete-draft"
                                    ? "bg-[color:var(--danger)] hover:bg-[color:color-mix(in_oklab,var(--danger)_85%,black)] text-white"
                                    : ""
                            }
                        >
                            {busy ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                            {t("confirm")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function SectionHeader({ icon: Icon, label, count }) {
    return (
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-[color:var(--brand-brown)]" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-[color:var(--ink-muted)]">
                {label}
            </h3>
            {count !== null ? (
                <span className="text-[10px] font-medium text-[color:var(--ink-muted)] bg-[color:var(--bg-surface-2)] rounded-full px-2 py-0.5">
                    {count}
                </span>
            ) : null}
        </div>
    );
}

function EmptyLine({ text }) {
    return (
        <div className="rounded-lg border border-dashed border-[color:var(--border-soft)] py-6 px-3 text-center text-xs text-[color:var(--ink-muted)]">
            {text}
        </div>
    );
}

function EmailStatusRow({ round, t }) {
    const en = round.email_notification;
    const status = en?.status || "none";
    const MAP = {
        sent: { Icon: MailCheck, tone: "text-[color:var(--success-soft-fg,#15803d)]", key: "qc_email_status_sent" },
        failed: { Icon: MailX, tone: "text-[color:var(--danger-soft-fg,#b91c1c)]", key: "qc_email_status_failed" },
        skipped_no_recipients: { Icon: MailWarning, tone: "text-[color:var(--ink-muted)]", key: "qc_email_status_skipped_no_recipients" },
        skipped_no_api_key: { Icon: MailWarning, tone: "text-[color:var(--danger-soft-fg,#b91c1c)]", key: "qc_email_status_skipped_no_api_key" },
        none: { Icon: Mail, tone: "text-[color:var(--ink-muted)]", key: "qc_email_status_none" },
    };
    const meta = MAP[status] || MAP.none;
    const Icon = meta.Icon;
    const reason = en?.reason || en?.error || "";
    return (
        <div
            data-testid={OPS.qcIns.rounds.emailStatus(round.id)}
            data-email-status={status}
            title={reason}
            className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium ${meta.tone}`}
        >
            <Icon className="h-3.5 w-3.5" />
            <span>{t("qc_email_status_label")}: {t(meta.key)}</span>
        </div>
    );
}

function RoundCard({ round, lang, t, onOpen, variant, canRoundAdmin, canDelete, canDeleteDraft, onEdit, onAction }) {
    const branch = lang === "ar" ? round.branch_name_ar : (round.branch_name_en || round.branch_name_ar);
    const isArchived = variant === "archived";
    const dateLabel =
        variant === "draft"
            ? new Date(round.updated_at || round.started_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")
            : new Date(round.submitted_at || round.started_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
                day: "numeric",
                month: "short",
                year: "numeric",
            });

    // Which pill(s) render on the card
    const statusPill = isArchived
        ? { label: t("qc_rounds_archived_badge"), tone: "secondary" }
        : variant === "draft"
            ? { label: t("qc_ins_status_draft"), tone: "warning" }
            : { label: t("qc_ins_status_submitted"), tone: "success" };

    return (
        <Card
            data-testid={OPS.qcIns.rounds.card(round.id)}
            className={`cursor-pointer hover:shadow-[var(--shadow-md)] transition-shadow relative ${
                isArchived ? "opacity-80" : ""
            }`}
        >
            {/* Card body — clicking anywhere except the menu opens the round */}
            <div
                role="button"
                tabIndex={0}
                onClick={onOpen}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
            >
                <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                            <h4 className="text-sm font-bold text-[color:var(--ink)] truncate">{branch}</h4>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[color:var(--ink-muted)]">
                                <span className="inline-flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    {round.inspector_name || "—"}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    <bdi dir="ltr">{dateLabel}</bdi>
                                </span>
                            </div>
                        </div>
                        <Badge variant={statusPill.tone} className="shrink-0">
                            {statusPill.label}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="pt-0 pb-3">
                    <div className="flex items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-1.5 text-xs text-[color:var(--ink)]">
                            <AlertTriangle className="h-3.5 w-3.5 text-[color:var(--brand-orange)]" />
                            <span className="font-semibold">{round.total_violations}</span>
                            <span className="text-[color:var(--ink-muted)]">{t("qc_ins_violations_short")}</span>
                        </div>
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-[color:var(--brand-orange)]">
                            {variant === "draft" ? t("qc_ins_resume") : t("qc_ins_view")}
                            {lang === "ar" ? <ArrowRight className="h-3.5 w-3.5 rotate-180" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </span>
                    </div>
                    {canRoundAdmin && variant === "submitted" ? (
                        <EmailStatusRow round={round} t={t} />
                    ) : null}
                </CardContent>
            </div>

            {/* Round-admin overflow menu — floated on top of the card
               so clicking it doesn't also fire the card's onOpen.
               A DRAFT round also shows a delete entry to its own inspector
               (opened a round by mistake) and to the QC manager. Permanent
               delete of a SUBMITTED round stays super-admin only. */}
            {canRoundAdmin || canDeleteDraft ? (
                <div className="absolute top-2 end-2 z-10">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                data-testid={OPS.qcIns.rounds.admin.menu(round.id)}
                                variant="ghost"
                                size="icon-sm"
                                aria-label="round actions"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                            >
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="w-56"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {isArchived ? (
                                <>
                                    <DropdownMenuItem
                                        data-testid={OPS.qcIns.rounds.admin.unarchive(round.id)}
                                        onClick={() => onAction("unarchive")}
                                        className="cursor-pointer gap-2"
                                    >
                                        <ArchiveRestore className="h-4 w-4 text-[color:var(--success)]" />
                                        {t("qc_admin_unarchive_action")}
                                    </DropdownMenuItem>
                                    {canDelete ? (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                data-testid={OPS.qcIns.rounds.admin.del(round.id)}
                                                onClick={() => onAction("delete")}
                                                className="cursor-pointer gap-2 text-[color:var(--danger)] focus:text-[color:var(--danger)]"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                {t("qc_admin_delete_action")}
                                            </DropdownMenuItem>
                                        </>
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    {canRoundAdmin ? (
                                        <>
                                            <DropdownMenuItem
                                                data-testid={OPS.qcIns.rounds.admin.edit(round.id)}
                                                onClick={() => onEdit(round)}
                                                className="cursor-pointer gap-2"
                                            >
                                                <Pencil className="h-4 w-4" />
                                                {t("qc_admin_edit_meta")}
                                            </DropdownMenuItem>
                                            {variant === "draft" ? (
                                                <DropdownMenuItem
                                                    data-testid={OPS.qcIns.rounds.admin.close(round.id)}
                                                    onClick={() => onAction("force-submit")}
                                                    className="cursor-pointer gap-2"
                                                >
                                                    <CheckCircle2 className="h-4 w-4 text-[color:var(--success)]" />
                                                    {t("qc_admin_close_action")}
                                                </DropdownMenuItem>
                                            ) : (
                                                <DropdownMenuItem
                                                    data-testid={OPS.qcIns.rounds.admin.reopen(round.id)}
                                                    onClick={() => onAction("reopen")}
                                                    className="cursor-pointer gap-2"
                                                >
                                                    <Undo2 className="h-4 w-4 text-[color:var(--brand-orange)]" />
                                                    {t("qc_admin_reopen_action")}
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuItem
                                                data-testid={OPS.qcIns.rounds.admin.archive(round.id)}
                                                onClick={() => onAction("archive")}
                                                className="cursor-pointer gap-2"
                                            >
                                                <Archive className="h-4 w-4 text-[color:var(--ink-muted)]" />
                                                {t("qc_admin_archive_action")}
                                            </DropdownMenuItem>
                                        </>
                                    ) : null}
                                    {canDelete || (variant === "draft" && canDeleteDraft) ? (
                                        <>
                                            {canRoundAdmin ? <DropdownMenuSeparator /> : null}
                                            <DropdownMenuItem
                                                data-testid={OPS.qcIns.rounds.admin.del(round.id)}
                                                onClick={() => onAction(variant === "draft" ? "delete-draft" : "delete")}
                                                className="cursor-pointer gap-2 text-[color:var(--danger)] focus:text-[color:var(--danger)]"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                {variant === "draft" ? t("qc_admin_delete_draft_action") : t("qc_admin_delete_action")}
                                            </DropdownMenuItem>
                                        </>
                                    ) : null}
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            ) : null}
        </Card>
    );
}
